import Config from 'src/config';
import { api, Api } from '../MainService/api';
import { ErrorKind, GeneralApiProblem } from '../MainService/apiProblem';
import { UserBase, UserToken } from 'src/interfaces';
import { DocumentContainerType } from 'src/enums/Documents';
import { BlockBlobClient } from '@azure/storage-blob';
import { ResponseKind } from '../MainService';
import {
  ChildCreationData,
  ChildrenData,
  ChildUpdationData,
} from 'src/interfaces/Children';
import { MessageList } from '../chat/chat.api.types';
import { ChatMessage, ChatSession, FlaggedData } from 'src/interfaces/Chat';
import { ApiResponse, GetChildProgressResponse } from './parent.api.types';

const PARENT_URL = Config.PARENT_URL;
const ADMIN_URL = Config.ADMIN_URL;
const USER_URL = Config.USER_URL;
// const AZURE_URL = "https://tutorbotdevstorage.blob.core.windows.net";

export interface GradeLevel {
  id: string;
  name: string;
  code: string;
}

export interface Subject {
  id: string;
  name: string;
  code: string;
  is_required: boolean;
}

export class ParentApi {
  private api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  async getGradeLevels() {
    try {
      const response = await this.api.apisauce.get<{ levels: GradeLevel[] }>(
        `${PARENT_URL}/grade-levels`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.error('Error fetching grade levels:', e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getParentGradeLevels() {
    try {
      const response = await this.api.apisauce.get<GradeLevel[]>(
        `${PARENT_URL}/all-child-levels`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.error('Error fetching grade levels:', e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getSubjectsByLevel(levelId: string) {
    try {
      const response = await this.api.apisauce.get<Subject[]>(
        `${USER_URL}/get-all-subjects?level_id=${levelId}`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.error('Error fetching subjects:', e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getSubjectsByGrade(gradeId: string) {
    try {
      const response = await this.api.apisauce.get<{ subjects: Subject[] }>(
        `${PARENT_URL}/subjects?grade_level_id=${gradeId}`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.error('Error fetching subjects:', e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getChildren() {
    try {
      const response = await this.api.apisauce.get<{
        children: ChildrenData[];
      }>(`${PARENT_URL}/list-children`);
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getChildChat(username: string, sessionId: string) {
    try {
      const response = await this.api.apisauce.get<{
        title: string;
        messages: ChatMessage[];
      }>(
        `${PARENT_URL}/view-child-chat-session?session_id=${sessionId}&child_username=${username}`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getChildSessions(
    username: string,
    subject?: string,
    level_id?: string
  ) {
    try {
      let url = `${PARENT_URL}/list-child-chat-sessions?child_username=${username}`;
      if (subject) {
        url += `&subject=${encodeURIComponent(subject)}`;
      }
      if (level_id) {
        url += `&level_id=${encodeURIComponent(level_id)}`;
      }
      const response = await this.api.apisauce.get<{
        chat_sessions: ChatSession[];
      }>(url);
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async setProfilePic(profilePic: File) {
    try {
      const formData = new FormData();
      formData.append('profile_picture', profilePic);

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const response = await this.api.apisauce.post(
        `${PARENT_URL}/register-child`,
        formData,
        config
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: 'Bad data error' };
    }
  }

  async checkPlanUpgrade(plan: number) {
    try {
      const response = await this.api.apisauce.post<{
        children: ChildrenData[];
      }>(`${PARENT_URL}/check-payment-upgrade?plan_num=${plan}`);
      let data = await this.api.getResponse(response, true);
      console.log('Data is', data);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: 'Bad data error' };
    }
  }

  async createChild(child: ChildCreationData, profilePic: File | null) {
    try {
      const formData = new FormData();
      console.log('child data: ', child);
      console.log('child subjects: ', typeof child.subjects[0]);

      // Append child data as JSON string
      formData.append('child_data', JSON.stringify(child));

      // Append profile picture if provided
      if (profilePic != null) {
        formData.append('profile_picture', profilePic);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const response = await this.api.apisauce.post<ChildrenData>(
        `${PARENT_URL}/register-child`,
        formData,
        config
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: 'Bad data error' };
    }
  }

  async flagMessage(flaggedData: FlaggedData) {
    try {
      const response = await this.api.apisauce.post(
        `${PARENT_URL}/flag-child-chat`,
        flaggedData
      );

      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async updateChild(
    childUsername: string,
    childUpdateData: Partial<ChildUpdationData>
  ) {
    try {
      const response = await this.api.apisauce.put(
        `${PARENT_URL}/update-child/${childUsername}`,
        childUpdateData
      );
      console.log('Updated data', childUpdateData);
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: 'Bad data error' };
    }
  }

  async setChildPreference(preference: number) {
    try {
      const response = await this.api.apisauce.post(
        `${PARENT_URL}/set-child-preference?child_preference=${preference}`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: 'Bad data error' };
    }
  }

  async deleteChild(username: string) {
    try {
      const response = await this.api.apisauce.get(
        `${PARENT_URL}/remove-child?child_username=${username}`
      );
      let data = await this.api.getResponse(response);

      return data;
    } catch (e) {
      // alert("You entered some bad data")
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: 'Bad Data' };
    }
  }

  async getChildProgress(id: number, subject: string, level: string) {
    try {
      const response = await this.api.apisauce.post(
        `${USER_URL}/get-child-progress`,
        { id, subject, level }
      );
      const data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: 'Bad Data' };
    }
  }
}

export const parentService = new ParentApi(api);
